import React from 'react';
import styled, { keyframes } from 'styled-components';
import { PlayCircleOutlined, AndroidOutlined } from '@ant-design/icons';

// Glow animation for the download button
const glow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(255, 77, 77, 0.5), 0 0 10px rgba(255, 77, 77, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 77, 77, 0.8), 0 0 30px rgba(255, 77, 77, 0.5);
  }
  100% {
    box-shadow: 0 0 5px rgba(255, 77, 77, 0.5), 0 0 10px rgba(255, 77, 77, 0.3);
  }
`;

const PageContainer = styled.div`
  background: linear-gradient(135deg, #0e1118 0%, #1a1f2c 100%);
  color: #f8f9fa;
  min-height: 100vh;
  padding: 40px 20px;
  font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const HeroBanner = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5);
  margin-bottom: 40px;
  background-image: url('/on_su_game_banner.png');
  background-size: cover;
  background-position: center;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 80%;
    background: linear-gradient(to top, rgba(14, 17, 24, 1) 0%, rgba(14, 17, 24, 0.7) 40%, rgba(14, 17, 24, 0) 100%);
  }
`;

const HeroText = styled.div`
  position: absolute;
  bottom: 30px;
  left: 30px;
  right: 30px;
  z-index: 2;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin: 0 0 10px 0;
  background: linear-gradient(to right, #ff4d4d, #ffcc00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 2px 2px 8px rgba(0,0,0,0.5);
  letter-spacing: 1px;
  
  @media (max-width: 600px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin: 0;
  color: #e2e8f0;
  text-shadow: 1px 1px 4px rgba(0,0,0,0.8);
  font-weight: 400;
  
  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const Badge = styled.span`
  background-color: #ffcc00;
  color: #1a1f2c;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  margin-right: 10px;
  letter-spacing: 0.5px;
  display: inline-block;
  margin-bottom: 10px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 30px;
  
  @media (max-width: 600px) {
    padding: 20px;
  }
`;

const Sidebar = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: fit-content;
  
  @media (max-width: 600px) {
    padding: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.6rem;
  margin-top: 0;
  margin-bottom: 20px;
  border-left: 4px solid #ff4d4d;
  padding-left: 12px;
  color: #f8f9fa;
`;

const Paragraph = styled.p`
  color: #a0aec0;
  line-height: 1.8;
  font-size: 1.05rem;
  margin-bottom: 20px;
`;

const FeatureList = styled.div`
  margin-top: 30px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 77, 77, 0.3);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.15rem;
  color: #ff4d4d;
  margin-top: 0;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FeatureDesc = styled.p`
  color: #a0aec0;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #718096;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: #e2e8f0;
  font-weight: 600;
`;

const DownloadButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: linear-gradient(135deg, #ff4d4d 0%, #cc0000 100%);
  color: white;
  text-decoration: none;
  padding: 15px 25px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  margin-top: 25px;
  transition: all 0.3s ease;
  animation: ${glow} 3s infinite;
  cursor: pointer;
  border: none;
  text-align: center;

  &:hover {
    transform: scale(1.03);
    background: linear-gradient(135deg, #ff6666 0%, #e60000 100%);
    color: white;
    text-decoration: none;
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const OnSuGame = () => {
  return (
    <PageContainer>
      <ContentWrapper>
        
        {/* Banner Hero */}
        <HeroBanner>
          <HeroText>
            <Badge>Android Game</Badge>
            <HeroTitle>ÔN SỬ</HeroTitle>
            <HeroSubtitle>Game đấu súng lịch sử tái hiện chiến dịch Điện Biên Phủ hào hùng</HeroSubtitle>
          </HeroText>
        </HeroBanner>

        <Grid>
          {/* Main Description */}
          <MainContent>
            <SectionTitle>Giới Thiệu Trò Chơi</SectionTitle>
            <Paragraph>
              <strong>Ôn Sử</strong> là một dự án game bắn súng góc nhìn thứ nhất (FPS) đầy tâm huyết, lấy bối cảnh cuộc chiến đấu lịch sử hào hùng của dân tộc Việt Nam - <strong>Chiến dịch Điện Biên Phủ (1954)</strong>. Trò chơi mang đến một cách tiếp cận hoàn toàn mới, sinh động và đầy cuốn hút đối với lịch sử nước nhà.
            </Paragraph>
            <Paragraph>
              Trong game, người chơi sẽ hóa thân thành các chiến sĩ Quân đội Nhân dân Việt Nam anh hùng, kề vai sát cánh cùng đồng đội vượt qua những chiến hào khói lửa, tham chiến tại các cứ điểm phòng ngự kiên cố của thực dân Pháp như đồi A1, Him Lam, Bản Kéo, Độc Lập...
            </Paragraph>

            <SectionTitle style={{ marginTop: '40px' }}>Đặc Điểm Nổi Bật</SectionTitle>
            <FeatureList>
              <FeatureCard>
                <FeatureTitle>
                  <PlayCircleOutlined /> Lối Chơi Kịch Tính
                </FeatureTitle>
                <FeatureDesc>
                  Bắn súng góc nhìn thứ nhất (FPS) kịch tính, đòi hỏi tư duy chiến thuật, tận dụng địa hình chiến hào, công sự phòng thủ.
                </FeatureDesc>
              </FeatureCard>

              <FeatureCard>
                <FeatureTitle>
                  <PlayCircleOutlined /> Bối Cảnh Lịch Sử
                </FeatureTitle>
                <FeatureDesc>
                  Tái hiện chân thực sơ đồ chiến hào, các cứ điểm quân sự lịch sử của tập đoàn cứ điểm Điện Biên Phủ.
                </FeatureDesc>
              </FeatureCard>

              <FeatureCard>
                <FeatureTitle>
                  <PlayCircleOutlined /> Đồ Họa Chân Thực
                </FeatureTitle>
                <FeatureDesc>
                  Thiết kế vũ khí, quân phục và trang bị thời kỳ kháng chiến chống Pháp chân thực, mang lại trải nghiệm sống động.
                </FeatureDesc>
              </FeatureCard>

              <FeatureCard>
                <FeatureTitle>
                  <PlayCircleOutlined /> Ý Nghĩa Giáo Dục
                </FeatureTitle>
                <FeatureDesc>
                  Không chỉ là giải trí, trò chơi lồng ghép những thông tin lịch sử hào hùng, giúp người chơi ôn lại bài học lịch sử dân tộc.
                </FeatureDesc>
              </FeatureCard>
            </FeatureList>
          </MainContent>

          {/* Info Sidebar */}
          <Sidebar>
            <div>
              <SectionTitle>Thông Tin Game</SectionTitle>
              <InfoRow>
                <InfoLabel>Thể loại</InfoLabel>
                <InfoValue>Đấu Súng / Lịch Sử</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Bối cảnh</InfoLabel>
                <InfoValue>Điện Biên Phủ (1954)</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Nền tảng</InfoLabel>
                <InfoValue>Android</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Ngôn ngữ</InfoLabel>
                <InfoValue>Tiếng Việt</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Nhà phát triển</InfoLabel>
                <InfoValue>Ôn Sử Team</InfoValue>
              </InfoRow>
            </div>

            <DownloadButton 
              href="https://play.google.com/store/apps/details?id=com.onsu.onsu" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <AndroidOutlined style={{ fontSize: '24px' }} />
              Tải Game trên Google Play
            </DownloadButton>
          </Sidebar>
        </Grid>

      </ContentWrapper>
    </PageContainer>
  );
};

export default OnSuGame;
